import type { MetricExplanation, MetricResult, MetricRole, MetricWarning } from '../contracts';
import { cac as cacFormula } from '../formulas/cac';
import { cpa as cpaFormula } from '../formulas/cpa';
import { cpl as cplFormula } from '../formulas/cpl';
import { projectedRevenue as projectedRevenueFormula } from '../formulas/revenue';
import { roas as roasFormula } from '../formulas/roas';
import { roi as roiFormula } from '../formulas/roi';
import { incompleteCostBasis, missingInput } from '../warnings';
import type { NodeMetricsResult } from '../node-metrics';

export interface AggregationNodeInput {
  id: string;
  roles: readonly MetricRole[];
  result: NodeMetricsResult;
  /** §28 — defaults to true for a `total_cost`-role node when unset. */
  includeInFunnelInvestment?: boolean;
}

interface SumResult {
  result: MetricResult;
  contributors: string[];
}

/**
 * SPEC-008 §21/§22/plan §22 — sums a field across nodes carrying a given
 * role. Never claims a partial sum is complete (a subset of contributing
 * nodes being unknown still returns the known partial, with a warning).
 */
function sumByRole(
  nodes: readonly AggregationNodeInput[],
  role: MetricRole,
  pick: (node: AggregationNodeInput) => number | null,
  metricName: string,
  partialWarningFactory: (refs: string[]) => MetricWarning,
  filter?: (node: AggregationNodeInput) => boolean,
): SumResult {
  const roleNodes = nodes.filter((node) => node.roles.includes(role) && (!filter || filter(node)));
  if (roleNodes.length === 0) {
    return { result: { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: [] }, contributors: [] };
  }
  const known = roleNodes.filter((node) => pick(node) !== null);
  const contributors = known.map((node) => node.id);
  const sum = known.reduce((total, node) => total + pick(node)!, 0);
  const explanation: MetricExplanation = {
    metric: metricName,
    formula: `sum(${role})`,
    inputs: {},
    result: known.length > 0 ? sum : null,
    contributors,
  };
  if (known.length === 0) {
    return {
      result: { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: [], explanation },
      contributors: [],
    };
  }
  const partial = known.length < roleNodes.length;
  const warnings = partial ? [partialWarningFactory(roleNodes.map((n) => n.id))] : [];
  return {
    result: { status: 'known', effectiveValue: sum, calculatedCandidate: sum, origin: 'calculated', warnings, explanation },
    contributors,
  };
}

export interface FunnelAggregateResult {
  investment: MetricResult;
  mediaInvestment: MetricResult;
  leads: MetricResult;
  projectedClients: MetricResult;
  revenue: MetricResult;
  projectedRevenue: MetricResult;
  cpl: MetricResult;
  cpa: MetricResult;
  cac: MetricResult;
  roas: MetricResult;
  roi: MetricResult;
  warnings: MetricWarning[];
}

/**
 * SPEC-008 §21-30/§59/plan §21-24 — funnel-level aggregation using explicit
 * metric roles only (METRIC-REQ-020: never inferred silently). Each sum's
 * `explanation.contributors` names exactly which nodes fed it (plan §22).
 */
export function aggregateFunnelMetrics(nodes: readonly AggregationNodeInput[]): FunnelAggregateResult {
  const warnings: MetricWarning[] = [];
  const collect = (r: SumResult | { result: MetricResult }) => {
    warnings.push(...r.result.warnings);
    return r.result;
  };

  const investment = collect(
    sumByRole(
      nodes,
      'total_cost',
      (n) => n.result.cost.effectiveValue as number | null,
      'investment',
      (refs) => incompleteCostBasis(undefined, refs),
      (n) => n.includeInFunnelInvestment !== false,
    ),
  );
  const mediaInvestmentSum = sumByRole(
    nodes,
    'media_cost',
    (n) => n.result.cost.effectiveValue as number | null,
    'mediaInvestment',
    (refs) => incompleteCostBasis(undefined, refs),
  );
  const mediaInvestment = collect(mediaInvestmentSum);

  const leads = collect(
    sumByRole(nodes, 'lead', (n) => n.result.exits.effectiveValue as number | null, 'leads', (refs) => missingInput(undefined, refs)),
  );
  const projectedClients = collect(
    sumByRole(
      nodes,
      'new_customer',
      (n) => n.result.exits.effectiveValue as number | null,
      'projectedClients',
      (refs) => missingInput(undefined, refs),
    ),
  );
  const revenueSum = sumByRole(
    nodes,
    'revenue',
    (n) => n.result.revenue.effectiveValue as number | null,
    'revenue',
    (refs) => missingInput(undefined, refs),
  );
  const revenue = collect(revenueSum);

  // §32/plan §24 — if no explicit revenue is known, fall back to
  // projected clients × the best available average ticket, clearly marked
  // as a calculated projection rather than confirmed revenue.
  let projectedRevenue: MetricResult;
  if (revenue.status === 'known') {
    projectedRevenue = revenue;
  } else {
    const ticketNode = nodes.find(
      (n) => (n.roles.includes('revenue') || n.roles.includes('new_customer')) && n.result.averageTicket.effectiveValue !== null,
    );
    const candidate = projectedRevenueFormula(
      projectedClients.effectiveValue as number | null,
      (ticketNode?.result.averageTicket.effectiveValue as number | null) ?? null,
    );
    projectedRevenue =
      candidate.value === null
        ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: candidate.warnings, explanation: candidate.explanation }
        : { status: 'known', effectiveValue: candidate.value, calculatedCandidate: candidate.value, origin: 'calculated', warnings: candidate.warnings, explanation: candidate.explanation };
    warnings.push(...candidate.warnings);
  }

  const cplCandidate = cplFormula(investment.effectiveValue as number | null, leads.effectiveValue as number | null);
  const cpl: MetricResult =
    cplCandidate.value === null
      ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: cplCandidate.warnings, explanation: cplCandidate.explanation }
      : { status: 'known', effectiveValue: cplCandidate.value, calculatedCandidate: cplCandidate.value, origin: 'calculated', warnings: cplCandidate.warnings, explanation: cplCandidate.explanation };
  warnings.push(...cplCandidate.warnings);

  // V1 simplification (no separate "target action" role exists in the
  // spec): funnel CPA reuses the same leads basis as CPL.
  const cpaCandidate = cpaFormula(investment.effectiveValue as number | null, leads.effectiveValue as number | null);
  const cpa: MetricResult =
    cpaCandidate.value === null
      ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: cpaCandidate.warnings, explanation: cpaCandidate.explanation }
      : { status: 'known', effectiveValue: cpaCandidate.value, calculatedCandidate: cpaCandidate.value, origin: 'calculated', warnings: cpaCandidate.warnings, explanation: cpaCandidate.explanation };
  warnings.push(...cpaCandidate.warnings);

  const incompleteBasis = investment.warnings.some((w) => w.code === 'incomplete_cost_basis');
  const cacCandidate = cacFormula(investment.effectiveValue as number | null, projectedClients.effectiveValue as number | null, incompleteBasis);
  const cac: MetricResult =
    cacCandidate.value === null
      ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: cacCandidate.warnings, explanation: cacCandidate.explanation }
      : { status: 'known', effectiveValue: cacCandidate.value, calculatedCandidate: cacCandidate.value, origin: 'calculated', warnings: cacCandidate.warnings, explanation: cacCandidate.explanation };
  warnings.push(...cacCandidate.warnings);

  const hasMediaCostRole = nodes.some((n) => n.roles.includes('media_cost'));
  const roasBasis = hasMediaCostRole ? mediaInvestment.effectiveValue : investment.effectiveValue;
  const roasCandidate = roasFormula(
    (revenue.status === 'known' ? revenue.effectiveValue : projectedRevenue.effectiveValue) as number | null,
    roasBasis as number | null,
    !hasMediaCostRole,
  );
  const roas: MetricResult =
    roasCandidate.value === null
      ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: roasCandidate.warnings, explanation: roasCandidate.explanation }
      : { status: 'known', effectiveValue: roasCandidate.value, calculatedCandidate: roasCandidate.value, origin: 'calculated', warnings: roasCandidate.warnings, explanation: roasCandidate.explanation };
  warnings.push(...roasCandidate.warnings);

  const roiCandidate = roiFormula(
    (revenue.status === 'known' ? revenue.effectiveValue : projectedRevenue.effectiveValue) as number | null,
    investment.effectiveValue as number | null,
  );
  const roi: MetricResult =
    roiCandidate.value === null
      ? { status: 'unknown', effectiveValue: null, calculatedCandidate: null, origin: null, warnings: roiCandidate.warnings, explanation: roiCandidate.explanation }
      : { status: 'known', effectiveValue: roiCandidate.value, calculatedCandidate: roiCandidate.value, origin: 'calculated', warnings: roiCandidate.warnings, explanation: roiCandidate.explanation };
  warnings.push(...roiCandidate.warnings);

  return {
    investment,
    mediaInvestment,
    leads,
    projectedClients,
    revenue,
    projectedRevenue,
    cpl,
    cpa,
    cac,
    roas,
    roi,
    warnings: dedupe(warnings),
  };
}

function dedupe(warnings: readonly MetricWarning[]): MetricWarning[] {
  const seen = new Set<string>();
  const out: MetricWarning[] = [];
  for (const w of warnings) {
    const key = `${w.code}:${(w.refs ?? []).join(',')}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(w);
  }
  return out;
}
